import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaRegClock, FaCog } from 'react-icons/fa';
import SubscriptionSettings from '@/components/SubscriptionSettings';
import { Subscription } from '@/types/models.types';
import { formatCurrencyToMajor } from '@/utils/formatCurrency';
import { formatTimestamp } from '@/utils/formatTimestamp';
import { SubscriptionCardStatusText, SubscriptionStatus, TrialStatus } from '@/constants/constants';

function SubscriptionCard({ subscription }: { subscription: Subscription }) {
  const { Expired, Cancelling, Paid, Trialing } = SubscriptionStatus;
  const { InTrial } = TrialStatus;
  const [showSettings, setShowSettings] = useState(false);

  const renderSubscriptionDetails = () => (
    <div className='my-4 mt-10'>
      {subscription.status === Paid && (
        <span
          data-cy='subscription-status'
          className='bg-green-200 text-green-700 text-xs font-medium me-2 px-2.5 py-0.5 rounded'
        >
          {SubscriptionCardStatusText.Paid}
        </span>
      )}
      {subscription.status === Cancelling && (
        <span
          data-cy='subscription-status'
          className='bg-yellow-200 text-yellow-700 text-xs font-medium me-2 px-2.5 py-0.5 pb-[3px] rounded'
        >
          {SubscriptionCardStatusText.Cancelling}
        </span>
      )}
      {subscription.status === Expired && (
        <span
          data-cy='subscription-status'
          className='bg-slate-200 text-slate-700 text-xs font-medium me-2 px-2.5 py-0.5 rounded'
        >
          {SubscriptionCardStatusText.Expired}
        </span>
      )}
      {subscription.trialStatus === InTrial && (
        <span
          data-cy='subscription-status'
          className='bg-yellow-200 text-yellow-700 text-xs font-bold me-2 px-2.5 py-0.5 rounded uppercase'
        >
          {SubscriptionCardStatusText.Trialing}
        </span>
      )}

      {subscription.trialStatus === InTrial ? (
        <p className='text-xl font-bold text-[#002327] mt-1'>
          <s>{formatCurrencyToMajor(subscription.product.price, 'gbp')}</s>
          <span className='text-sm font-normal text-slate-500'> / month</span>
        </p>
      ) : (
        <p className='text-xl font-bold text-[#002327] mt-1'>
          {formatCurrencyToMajor(subscription.product.price, 'gbp')}
          <span className='text-sm font-normal text-slate-500'> / month</span>
        </p>
      )}

      {subscription.status === Cancelling && (
        <p className='text-sm text-slate-500'>
          <FaRegClock style={{ verticalAlign: 'middle', display: 'inline-block', marginRight: '2px' }} /> Ends on{' '}
          {formatTimestamp(subscription.periodEnd)}
        </p>
      )}
      {subscription.status === Expired && (
        <p className='text-sm text-slate-500'>Ended on: {formatTimestamp(subscription.lastPayment)}</p>
      )}
      {subscription.status === Paid && (
        <p className='text-sm text-slate-500'>Next payment on {formatTimestamp(subscription.periodEnd)}</p>
      )}
      {subscription.status === Trialing && (
        <p className='text-sm text-slate-500'>First payment on {formatTimestamp(subscription.periodEnd)}</p>
      )}
    </div>
  );

  return (
    <div data-cy='subscription-card' key={subscription.id} className='bg-white rounded-md drop-shadow-2xl shadow p-8'>
      {showSettings ? (
        <SubscriptionSettings
          subscription={subscription}
          authorName={subscription.author.name}
          onClose={() => setShowSettings(false)}
        />
      ) : (
        <div className='flex flex-col h-full relative'>
          <div className='flex items-center'>
            <img
              className='min-w-16 min-h-16 w-16 h-16 basis-[64px] object-cover rounded-full mr-4'
              src={subscription.author.image || undefined}
              alt='Profile'
              loading='lazy'
            />
            <Link to={`/author/${subscription.author.lookupKey}`} className='hover:text-teal-700'>
              <h3 className='text-lg leading-6 font-semibold m-0 2xl:text-xl'>{subscription.author.name}</h3>
            </Link>

            {subscription.status !== Expired && (
              <button
                data-cy='open-settings'
                onClick={() => setShowSettings(true)}
                aria-label='Open subscription settings'
                className='absolute flex items-center justify-center w-[34px] h-[34px] -right-3 -top-[8px] p-2 rounded-md hover:bg-gray-100 transition-colors'
              >
                <FaCog className='w-[18px] h-[18px] text-gray-600' />
              </button>
            )}
          </div>
          <p className='mt-8 grow'>{subscription.product.description}</p>
          {renderSubscriptionDetails()}
        </div>
      )}
    </div>
  );
}

export default SubscriptionCard;
